import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Search, Trash2, Users, ShieldCheck, User as UserIcon, ArrowLeft } from "lucide-react";

const RoleBadge = ({ role }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${role === "admin" ? "bg-navy-900 text-white" : "bg-[#EEF2F8] text-navy-700"}`}>
    {role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />} {role === "admin" ? "Admin" : "Applicant"}
  </span>
);

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (roleFilter !== "all") params.role = roleFilter;
      const { data } = await adminApi.listUsers(params);
      setUsers(data.users || []);
    } catch {
      toast.error("Could not load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter]);

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    setSavingId(u.id);
    try {
      await adminApi.updateUser(u.id, { role });
      toast.success(`${u.email} is now ${role === "admin" ? "an admin" : "an applicant"}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update role"); }
    finally { setSavingId(null); }
  };

  const toggleActive = async (u) => {
    setSavingId(u.id);
    try {
      await adminApi.updateUser(u.id, { active: !(u.active !== false) });
      toast.success(`${u.email} ${u.active !== false ? "disabled" : "enabled"}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update account"); }
    finally { setSavingId(null); }
  };

  const remove = async (u) => {
    setSavingId(u.id);
    try {
      const { data } = await adminApi.deleteUser(u.id);
      toast.success(`Deleted ${u.email}${data.deleted_applications ? ` and ${data.deleted_applications} application(s)` : ""}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete user"); }
    finally { setSavingId(null); }
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="User accounts" subtitle="Manage user accounts, roles, and access to the WECH-RB platform." />
      <section className="wechr-section">
        <div className="wechr-container">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="admin-users-back"><ArrowLeft className="mr-1 h-4 w-4" /> Back to dashboard</Button>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--wechr-muted-text)]" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name, email, company" className="w-64 pl-9" data-testid="admin-users-search" />
              </div>
              <div className="w-40">
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger data-testid="admin-users-role-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                    <SelectItem value="applicant">Applicants</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={load}>Search</Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 border-b border-[var(--wechr-border)] p-4">
                <Users className="h-4 w-4 text-wgreen-600" />
                <h3 className="font-heading text-base font-semibold text-navy-900">All users {users.length ? `(${users.length})` : ""}</h3>
              </div>
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>
              ) : users.length === 0 ? (
                <p className="py-16 text-center text-sm text-[var(--wechr-muted-text)]">No users match your search.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="admin-users-table">
                    <TableHeader>
                      <TableRow className="[&_th]:text-xs [&_th]:uppercase [&_th]:tracking-wide">
                        <TableHead>User</TableHead><TableHead>Company</TableHead><TableHead>Role</TableHead>
                        <TableHead>Apps</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => {
                        const isSelf = me?.id === u.id;
                        const disabled = u.active === false;
                        return (
                          <TableRow key={u.id} data-testid="admin-users-row" className={disabled ? "opacity-60" : ""}>
                            <TableCell>
                              <p className="font-medium text-navy-900">{u.name} {isSelf && <span className="ml-1 rounded bg-wgreen-100 px-1.5 py-0.5 text-[10px] font-semibold text-wgreen-700">You</span>}</p>
                              <p className="text-xs text-[var(--wechr-muted-text)]">{u.email}</p>
                            </TableCell>
                            <TableCell className="text-[var(--wechr-muted-text)]">{u.company_name || "—"}</TableCell>
                            <TableCell>
                              {isSelf ? <RoleBadge role={u.role} /> : (
                                <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={savingId === u.id}>
                                  <SelectTrigger className="h-8 w-32 text-xs" data-testid={`admin-user-role-${u.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="applicant">Applicant</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </TableCell>
                            <TableCell className="tabular text-navy-800">{u.application_count ?? 0}</TableCell>
                            <TableCell>
                              <Switch checked={!disabled} onCheckedChange={() => toggleActive(u)} disabled={isSelf || savingId === u.id} data-testid={`admin-user-active-${u.id}`} />
                            </TableCell>
                            <TableCell className="text-right">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="outline" className="text-[#B42318] hover:bg-[#FEE4E2]" disabled={isSelf || savingId === u.id} data-testid={`admin-user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This permanently deletes <span className="font-medium">{u.email}</span> and their {u.application_count ?? 0} application(s) and uploaded documents. Issued certificates are kept as public records. This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction className="bg-[#B42318] text-white hover:bg-[#8f1c13]" onClick={() => remove(u)} data-testid={`admin-user-delete-confirm-${u.id}`}>Delete user</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AdminUsers;
